import {
  addToast,
  Button,
  ErrorToast,
  SuccessToast,
} from '@otter-ai/den-components';
import { GoogleDriveIcon } from '@otter-ai/den-icons';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import { FormattedText } from '@/shared/components/FormattedText';
import { useApplications } from '@/shared/hooks/useApplications';
import { openConfirmDialog } from '@/shared/modals/confirm/openConfirmDialog';
import { descriptionFormatRules } from '../../shared/utils/text';
import { AppRowLayout } from '../components/AppRowLayout';
import { useDisconnectApplication } from '../hooks/useDisconnectApplication';
import { useGoogleDocsOAuth } from '../hooks/useGoogleDocsOAuth';
import { GOOGLE_DOCS_TEST_IDS } from '../test-ids/googleDocs';
import { DisconnectedButton } from './DisconnectedButton';

/**
 * The row for Google Docs on the apps settings page.
 */
const APP_NAME = 'google-drive';

export function GoogleDocsAppRow() {
  const t = useTranslations('settings:integrations.apps.google_docs');
  const tCommon = useTranslations('settings:integrations');
  const { isApplicationConnected, isLoading: isLoadingStatus } =
    useApplications();
  const { mutateAsync: disconnectApp, isPending: isDisconnecting } =
    useDisconnectApplication(APP_NAME);
  const { openGoogleDocsAuthorizationPage } = useGoogleDocsOAuth();

  const isConnected = isApplicationConnected(APP_NAME);

  const onClickConnect = useCallback(async () => {
    try {
      openGoogleDocsAuthorizationPage();
    } catch {
      addToast(
        <ErrorToast
          description={tCommon('connectFailed', { appName: 'Google Docs' })}
        />,
      );
    }
  }, [openGoogleDocsAuthorizationPage, tCommon]);

  const handleDisconnect = useCallback(async () => {
    const confirmed = await openConfirmDialog({
      title: t('disconnectModal.title'),
      message: (
        <FormattedText>
          {t.rich('disconnectModal.description', descriptionFormatRules())}
        </FormattedText>
      ),
      confirmText: t('disconnectModal.confirmText'),
      cancelText: t('disconnectModal.cancelText'),
      danger: true,
    });

    if (!confirmed) {
      return true;
    }

    try {
      await disconnectApp();
      addToast(<SuccessToast description={t('successfulDisconnected')} />);
      return true;
    } catch {
      addToast(<ErrorToast description={t('failedDisconnect')} />);
      return false;
    }
  }, [t, disconnectApp]);

  const description = (
    <FormattedText>
      {t.rich('description', descriptionFormatRules({}))}
    </FormattedText>
  );

  return (
    <AppRowLayout
      appRowId="google-docs-app"
      Icon={GoogleDriveIcon}
      title="Google Drive"
      description={description}
      buttons={
        isLoadingStatus || isDisconnecting ? (
          <Button disabled>Loading...</Button>
        ) : isConnected ? (
          <DisconnectedButton
            disconnectButtonText={t('disconnectButton')}
            disconnectAction={handleDisconnect}
            tooltipCaption={t('disconnectTooltip')}
            data-testid={GOOGLE_DOCS_TEST_IDS.disconnectButton}
          />
        ) : (
          <Button
            onClick={onClickConnect}
            data-testid={GOOGLE_DOCS_TEST_IDS.connectButton}
          >
            {t('addButton')}
          </Button>
        )
      }
    />
  );
}
